import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import {
  useRejectFundingRequestMutation,
  GetFundingRequestWithBaseDetailsDocumentGQLType,
  GetFundingRequestDocumentGQLType,
  GetCurrentFundingRequestDocumentGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { GetCurrentLiabilityAccountsAndAccrualsForLoanDocumentGQLType } from 'packages/loans/requests/loans.gql.generated';
import { useApproveFundingRequestMutation } from '~/capitalProvider/domains/fundingRequests/requests/fundingRequests.gql.generated';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from '~/packages/common-frontend/constants';

type ActionableFundingRequest = {
  id: string;
  creditFacilityId: string;
  creditFacility: { name: string };
};

type Props = {
  fundingRequest: ActionableFundingRequest;
  disabled?: boolean;
};
export const ApproveFundingRequestButton = ({
  fundingRequest,
  disabled,
}: Props) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [approveFundingRequest, { loading }] = useApproveFundingRequestMutation(
    {
      variables: { fundingRequestId: fundingRequest.id },
      // Should likely reassess all these refetches and simplify
      refetchQueries: [
        {
          query: GetFundingRequestWithBaseDetailsDocumentGQLType,
          variables: { fundingRequestId: fundingRequest.id },
          fetchPolicy: 'network-only',
        },
        {
          query: GetFundingRequestDocumentGQLType,
          variables: { fundingRequestId: fundingRequest.id },
          fetchPolicy: 'network-only',
        },
        {
          query: GetCurrentFundingRequestDocumentGQLType,
          variables: { creditFacilityId: fundingRequest.creditFacilityId },
          fetchPolicy: 'network-only',
        },
        GetCurrentLiabilityAccountsAndAccrualsForLoanDocumentGQLType,
      ],
    },
  );

  const cancelRef = React.useRef(null);

  return (
    <>
      <Button
        colorScheme="aurora"
        variant="solid"
        aria-label="approve funding request"
        onClick={onOpen}
        isDisabled={!!disabled}
      >
        Approve
      </Button>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Approval
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you would like to approve the Funding Request for{' '}
              {fundingRequest.creditFacility.name}?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onClose}
                variant="outline"
                colorScheme="aurora"
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                isLoading={loading}
                colorScheme="aurora"
                variant="solid"
                onClick={async () => {
                  try {
                    const { data } = await approveFundingRequest();

                    if (data) {
                      successToastConfig.title = 'Funding Request Approved';
                      toast(successToastConfig);
                    }
                  } catch (err: any) {
                    if (!toast.isActive(errorToastId)) {
                      errorToastConfig.title = `Error approving funding request`;
                      toast(errorToastConfig);
                    }
                    onClose();
                    throw err;
                  }
                }}
                ml={3}
              >
                Confirm Approval
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
type RejectFundingRequestProps = {
  fundingRequest: ActionableFundingRequest;
  disabled?: boolean;
};
export const RejectFundingRequestButton = ({
  fundingRequest,
  disabled,
}: RejectFundingRequestProps) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [rejectFundingRequest, { loading }] = useRejectFundingRequestMutation({
    variables: { fundingRequestId: fundingRequest.id },
    // Should likely reassess all these refetches and simplify
    refetchQueries: [
      {
        query: GetFundingRequestWithBaseDetailsDocumentGQLType,
        variables: { fundingRequestId: fundingRequest.id },
        fetchPolicy: 'network-only',
      },
      {
        query: GetFundingRequestDocumentGQLType,
        variables: { fundingRequestId: fundingRequest.id },
        fetchPolicy: 'network-only',
      },
      {
        query: GetCurrentFundingRequestDocumentGQLType,
        variables: { creditFacilityId: fundingRequest.creditFacilityId },
        fetchPolicy: 'network-only',
      },
    ],
  });

  const cancelRef = React.useRef(null);

  return (
    <>
      <Button
        colorScheme="aurora"
        aria-label="reject funding request"
        onClick={onOpen}
        isDisabled={!!disabled}
        variant="outline"
      >
        Reject
      </Button>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Rejection
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to reject the Funding Request for{' '}
              {fundingRequest.creditFacility.name}?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onClose}
                colorScheme="aurora"
                variant="outline"
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                isLoading={loading}
                colorScheme="aurora"
                variant="solid"
                onClick={async () => {
                  try {
                    const { data } = await rejectFundingRequest();

                    if (data) {
                      successToastConfig.title = 'Funding Request Rejected';
                      toast(successToastConfig);
                    }
                  } catch (err: any) {
                    if (!toast.isActive(errorToastId)) {
                      errorToastConfig.title = `Error rejecting funding request`;
                      toast(errorToastConfig);
                    }
                    onClose();
                    throw err;
                  }
                }}
                ml={3}
              >
                Confirm Rejection
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
